// Research Page Types

export interface SearchFilters {
  names: string[];
  companies: string[];
  titles: string[];
  seniorities: string[];
  departments: string[];
  industries: string[];
  locations: string[];
  locationScope?: 'both_or' | 'both_and' | 'company' | 'contact';
  locationRadiusMiles?: number;
  employeeSizes: string[];
  estimatedRevenues: string[];
  keywords: string[];
  anyFormerCompanyMatch?: boolean;
  companiesBulkId?: string[];
  companiesExactMatch?: boolean;
  companyFoundedOn?: string[];
  companyFundingTotals?: Array<[string | null, string | null]>;
  companyLatestFundingClassifications?: string[];
  companyLatestFundingDates?: string[];
  companyTypes?: string[];
  startedCurrentJobDateFrom?: string;
  startedCurrentJobDateTo?: string;
  titleYearsMin?: number | null;
  titleYearsMax?: number | null;
  skills?: string[];
  skillsMode?: 'or' | 'and';
  formerCompanies?: string[];
  formerCompaniesBulkId?: string[];
  formerCompaniesExactMatch?: boolean;
  includeActivities?: boolean;
  includeCompanyAliases?: boolean;
  includeEducation?: boolean;
  includePositions?: boolean;
  includeTasks?: boolean;
  industryNaicsCodes?: string[];
  industrySicCodes?: string[];
  jobChangesDayRange?: string[];
  jobChangesType?: string[];
  jobChangesTypeMode?: 'or' | 'and';
  keywordsIsOr?: boolean;
  namesBulkId?: string[];
  overallSearch?: boolean;
  sortColumn?: string | null;
  sortOrder?: string | null;
  technologies?: string[];
  technologiesIsOr?: boolean;
  timezones?: number[];
  timezonesTypes?: string[];
  titlesBulkId?: string[];
  titlesExactMatch?: boolean;
  zipCodes?: string[];
  zipCodesRadius?: number[];
  zipCodesTypes?: string[];
  contactSearchId?: number | null;
  nextToken?: string | null;
  page?: number;
}

export interface Lead {
  searchResultId: string;
  contactSearchId: number;
  uiMatchId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  title?: string;
  titleStartedAt?: string;
  company?:
    | {
    name?: string;
    industry?: string;
    location?: string;
    linkedInUrl?: string;
  }
    | string;
  companyName?: string;
  Company?: string;
  industry?: string;
  location?: string;
  city?: string;
  state?: string;
  country?: string;
  companyCity?: string;
  companyState?: string;
  companyCountry?: string;
  seniority?: string;
  formerCompany?: string;
  formerEndedAt?: string;
  jobChangeDifferenceType?: string;
  jobChangeAlert?: string;
  jobChangeFormerCompany?: string;
  jobChangeFormerEndedAt?: string;
  domain?: string;
  contactPhone1?: string;
  contactPhone2?: string;
  companyPhone1?: string;
  businessEmail1?: string;
  email?: string;
  personalEmail1?: string;
  linkedInUrl?: string;
  liUrl?: string;
  companyLinkedInUrl?: string;
  CompanyLIProfileUrl?: string;
  researchedData?: ResearchedContact;
}

export interface ResearchedContact {
  contactPhone1?: string;
  companyPhone1?: string;
  Email?: string;
  PersonalEmail?: string;
  LIProfileUrl?: string;
  [key: string]: unknown;
}

export interface LinkedInScrapedData {
  scrapedAt: string;
  personData: unknown[];
  companyData: unknown[];
  raw: unknown[];
}

export interface SavedSearch {
  id: string;
  name: string;
  filters: SearchFilters;
  savedAt: string;
  lastPage?: number;
  perPage?: number;
}

export interface RecentSearch {
  filters: SearchFilters;
  searchedAt: string;
}

export interface ScheduledSearch {
  name: string;
  userEmail: string;
  filters: SearchFilters;
  scheduleDays: string[];
  scheduleHour: number;
  scheduleMinute: number;
  creditLimit: number;
  researchMode?: 'regular_pepai' | 'super_scrape';
  instantlyCampaignId?: string;
  instantlyCampaignName?: string;
  targetGhlLocationId?: string;
  ghlTargetLocationId?: string;
  perPage?: number;
  timezone?: string;
}

export const SENIORITY_OPTIONS = [
  'C-Level', 'VP', 'Director', 'Manager', 'Senior', 'Mid-Level', 'Entry Level', 'Other'
];

export const DEPARTMENT_OPTIONS = [
  'Sales', 'Marketing', 'Human Resources', 'IT', 'Engineering', 'Operations',
  'Finance', 'Support', 'Legal', 'Project Management', 'Other'
];

export const INDUSTRY_GROUP_OPTIONS: Array<{ category: string; subcategories: string[] }> = [
  { category: 'Aerospace & Defense', subcategories: ['Airlines & Aviation', 'Aviation & Aerospace', 'Defense & Space', 'Military'] },
  { category: 'Agriculture', subcategories: ['Farming', 'Horticulture', 'Ranching', 'Tobacco'] },
  { category: 'Apparel & Fashion', subcategories: ['Apparel & Fashion (subcategory)', 'Textiles'] },
  { category: 'Automotive', subcategories: ['Automotive (subcategory)'] },
  { category: 'Chemicals & Materials', subcategories: ['Chemicals', 'Plastics'] },
  { category: 'Consumer Goods & Retail', subcategories: ['Consumer Goods', 'Luxury Goods & Jewelry', 'Retail', 'Sporting Goods'] },
  { category: 'Education & Training', subcategories: ['E-Learning', 'Education Management', 'Higher Education', 'Libraries', 'Primary/Secondary Education'] },
  { category: 'Electronics & Hardware', subcategories: ['Computer Hardware', 'Consumer Electronics', 'Electrical & Electronic Manufacturing', 'Semiconductors'] },
  { category: 'Energy & Utilities', subcategories: ['Oil & Energy', 'Utilities'] },
  {
    category: 'Entertainment',
    subcategories: [
      'Animation',
      'Arts & Crafts',
      'Computer Games',
      'Entertainment (subcategory)',
      'Fine Art',
      'Gambling & Casinos',
      'Mobile Games',
      'Motion Pictures & Film',
      'Music',
      'Performing Arts',
      'Photography',
      'Recreational Facilities & Services',
      'Sports',
    ],
  },
  { category: 'Environmental', subcategories: ['Environmental Services', 'Renewables & Environment'] },
  {
    category: 'Finance & Banking',
    subcategories: ['Banking', 'Capital Markets', 'Financial Services', 'Investment Banking', 'Investment Management', 'Venture Capital & Private Equity'],
  },
  { category: 'Food & Beverage', subcategories: ['Dairy', 'Fishery', 'Food & Beverages', 'Food Production', 'Restaurants', 'Supermarkets', 'Wine & Spirits'] },
  {
    category: 'Government & Public Policy',
    subcategories: ['Executive Office', 'Government Administration', 'Government Relations', 'Judiciary', 'Law Enforcement', 'Legislative Office', 'Political Organization', 'Public Policy', 'Public Safety'],
  },
  { category: 'Health & Wellness', subcategories: ['Alternative Medicine', 'Health, Wellness & Fitness', 'Hospital & Health Care', 'Medical Practice', 'Mental Health Care', 'Veterinary'] },
  { category: 'Hospitality & Tourism', subcategories: ['Events Services', 'Hospitality', 'Leisure, Travel & Tourism', 'Museums & Institutions'] },
  { category: 'Household, Personal, & Beauty', subcategories: ['Consumer Services', 'Cosmetics', 'Furniture', 'Individual & Family Services'] },
  { category: 'Insurance', subcategories: ['Insurance (subcategory)'] },
  { category: 'Internet & E-Commerce', subcategories: ['Internet'] },
  { category: 'Manufacturing & Engineering', subcategories: ['Civil Engineering', 'Industrial Automation', 'Machinery', 'Mechanical or Industrial Engineering', 'Railroad Manufacture', 'Shipbuilding'] },
  {
    category: 'Marketing & Media',
    subcategories: ['Broadcast Media', 'Graphic Design', 'Marketing & Advertising', 'Media Production', 'Newspapers', 'Online Media', 'Printing', 'Public Relations & Communications', 'Publishing', 'Writing & Editing'],
  },
  { category: 'Metals, Mining & Materials', subcategories: ['Building Materials', 'Glass, Ceramics & Concrete', 'Mining & Metals', 'Paper & Forest Products'] },
  { category: 'Non-Profit', subcategories: ['Fund-Raising', 'Non-Profit Organization Management', 'Philanthropy', 'Religious Institutions'] },
  { category: 'Pharmaceuticals & Medical Devices', subcategories: ['Biotechnology', 'Medical Devices', 'Nanotechnology', 'Pharmaceuticals'] },
  {
    category: 'Professional Services & Consulting',
    subcategories: [
      'Accounting',
      'Alternative Dispute Resolution',
      'Civic & Social Organization',
      'Design',
      'Human Resources',
      'International Affairs',
      'International Trade & Development',
      'Law Practice',
      'Legal Services',
      'Management Consulting',
      'Market Research',
      'Outsourcing/Offshoring',
      'Professional Training & Coaching',
      'Program Development',
      'Research',
      'Security & Investigations',
      'Staffing & Recruiting',
      'Think Tanks',
    ],
  },
  { category: 'Real Estate & Construction', subcategories: ['Architecture & Planning', 'Commercial Real Estate', 'Construction', 'Facilities Services', 'Real Estate'] },
  { category: 'Software & Information Technology', subcategories: ['Computer & Network Security', 'Computer Software', 'Information Services', 'Information Technology & Services', 'Software Development'] },
  { category: 'Telecommunications & Networking', subcategories: ['Computer Networking', 'Telecommunications', 'Wireless'] },
  { category: 'Transportation & Logistics', subcategories: ['Logistics & Supply Chain', 'Maritime', 'Package/Freight Delivery', 'Packaging & Containers', 'Translation & Localization', 'Transportation/Trucking/Railroad'] },
  { category: 'Wholesale & Distribution', subcategories: ['Business Supplies & Equipment', 'Import & Export', 'Warehousing', 'Wholesale'] },
];

export const INDUSTRY_OPTIONS = INDUSTRY_GROUP_OPTIONS.flatMap((group) => [group.category, ...group.subcategories]);

export const EMPLOYEE_SIZE_OPTIONS = [
  '0 - 1 (Self-employed)', '2 - 10', '11 - 50', '51 - 200', '201 - 500',
  '501 - 1,000', '1,001 - 5,000', '5,001 - 10,000', '10,001+'
];

export const REVENUE_OPTIONS = [
  '$0 - $100K', '$100K - $1M', '$1M - $5M', '$5M - $20M', '$20M - $50M',
  '$50M - $100M', '$100M - $500M', '$500M - $1B', '$1B+'
];
